// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: while the mobile software keyboard is open the document carries
// `mobile-keyboard-open`. Installed iOS apps also carry the legacy
// `ios-keyboard-open` class. Surfaces that reserve the
// home-indicator inset drop it for as long as that class is set: the keyboard
// covers the home indicator, but iOS keeps reporting the old
// `safe-area-inset-bottom` until the next full relayout, so the reserved strip
// would otherwise sit under the keyboard as dead space. See
// ChannelChatLayout.module.css.

import {isInstalledIOSPwa} from '@app/features/ui/utils/PwaUtils';
import {useEffect} from 'react';

export const IOS_KEYBOARD_OPEN_CLASS = 'ios-keyboard-open';
export const MOBILE_KEYBOARD_OPEN_CLASS = 'mobile-keyboard-open';

const KEYBOARD_HEIGHT_THRESHOLD = 120;
// How long the installed iOS app assumes a keyboard is coming after focus
// lands without the viewport showing one (hardware keyboards never do).
const KEYBOARD_ARRIVAL_GRACE_MS = 1000;

function isEditable(target: EventTarget | null): boolean {
	if (!(target instanceof HTMLElement)) return false;
	if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return !target.readOnly;
	return target.isContentEditable;
}

export function useIosKeyboardAccessory(): void {
	useEffect(() => {
		const root = document.documentElement;
		const viewport = window.visualViewport;
		const installedIos = isInstalledIOSPwa();
		let editableFocused = isEditable(document.activeElement);
		let focusFrame: number | null = null;
		let viewportBaseline = Math.max(window.innerHeight, viewport?.height ?? 0);
		let keyboardSeen = false;
		let arrivalTimer: ReturnType<typeof setTimeout> | null = null;
		const keyboardTakesViewport = () =>
			viewport != null && viewportBaseline - viewport.height > KEYBOARD_HEIGHT_THRESHOLD;
		const syncKeyboardClass = () => {
			// WebKit fires no focusout when the focused composer is unmounted (leaving
			// a chat, closing a sheet), so the cached flag can outlive the keyboard and
			// leave the rail hidden. Trust the live focus unless a focusout is settling.
			if (editableFocused && focusFrame == null && !isEditable(document.activeElement)) editableFocused = false;
			if (!editableFocused) {
				viewportBaseline = Math.max(window.innerHeight, viewport?.height ?? 0);
				keyboardSeen = false;
			}
			const viewportShowsKeyboard = keyboardTakesViewport();
			if (editableFocused && viewportShowsKeyboard) keyboardSeen = true;
			// The installed iOS app hides the rail as soon as focus lands so the
			// keyboard never slides up over it. Once the viewport has shown the
			// keyboard, trust the viewport: back at full height means the keyboard went
			// away (swipe-down, hardware keyboard) even if focus stayed put.
			const assumeKeyboard = installedIos && (viewport == null || (!keyboardSeen && arrivalTimer != null));
			const mobileKeyboardOpen = editableFocused && (viewportShowsKeyboard || assumeKeyboard);
			root.classList.toggle(MOBILE_KEYBOARD_OPEN_CLASS, mobileKeyboardOpen);
			root.classList.toggle(IOS_KEYBOARD_OPEN_CLASS, installedIos && editableFocused);
		};
		// The visual viewport scrolls and resizes many times while you type on iOS (Safari keeps
		// the caret in view). A custom property on <html> restyles the whole document, so only
		// write it when the value actually changed.
		let writtenHeight: string | null = null;
		let writtenTop: string | null = null;
		const writeViewportVars = (height: string | null, top: string | null) => {
			if (height !== writtenHeight) {
				if (height == null) root.style.removeProperty('--ios-keyboard-viewport-height');
				else root.style.setProperty('--ios-keyboard-viewport-height', height);
				writtenHeight = height;
			}
			if (top !== writtenTop) {
				if (top == null) root.style.removeProperty('--ios-keyboard-viewport-top');
				else root.style.setProperty('--ios-keyboard-viewport-top', top);
				writtenTop = top;
			}
		};
		const syncViewport = () => {
			syncKeyboardClass();
			if (!viewport || !root.classList.contains(IOS_KEYBOARD_OPEN_CLASS)) {
				writeViewportVars(null, null);
				return;
			}
			writeViewportVars(`${viewport.height}px`, `${viewport.offsetTop}px`);
		};
		const onFocusIn = (event: FocusEvent) => {
			if (focusFrame != null) cancelAnimationFrame(focusFrame);
			focusFrame = null;
			if (isEditable(event.target) && !editableFocused) {
				editableFocused = true;
				if (arrivalTimer != null) clearTimeout(arrivalTimer);
				arrivalTimer = setTimeout(() => {
					arrivalTimer = null;
					syncViewport();
				}, KEYBOARD_ARRIVAL_GRACE_MS);
			}
			syncViewport();
		};
		const onFocusOut = (event: FocusEvent) => {
			if (isEditable(event.relatedTarget)) return;
			// Let focus settle before deciding that the keyboard closed. This avoids
			// flashing the rail while focus moves between composer controls.
			focusFrame = requestAnimationFrame(() => {
				focusFrame = null;
				editableFocused = isEditable(document.activeElement);
				syncViewport();
			});
		};
		// Backstops for anything the events above miss: re-check on the next touch
		// and when the app comes back to the foreground.
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') syncViewport();
		};
		syncViewport();
		viewport?.addEventListener('resize', syncViewport);
		viewport?.addEventListener('scroll', syncViewport);
		document.addEventListener('focusin', onFocusIn);
		document.addEventListener('focusout', onFocusOut);
		document.addEventListener('pointerdown', syncViewport, true);
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => {
			document.removeEventListener('focusin', onFocusIn);
			document.removeEventListener('focusout', onFocusOut);
			document.removeEventListener('pointerdown', syncViewport, true);
			document.removeEventListener('visibilitychange', onVisibilityChange);
			viewport?.removeEventListener('resize', syncViewport);
			viewport?.removeEventListener('scroll', syncViewport);
			if (focusFrame != null) cancelAnimationFrame(focusFrame);
			if (arrivalTimer != null) clearTimeout(arrivalTimer);
			editableFocused = false;
			root.classList.remove(IOS_KEYBOARD_OPEN_CLASS);
			root.classList.remove(MOBILE_KEYBOARD_OPEN_CLASS);
			root.style.removeProperty('--ios-keyboard-viewport-height');
			root.style.removeProperty('--ios-keyboard-viewport-top');
		};
	}, []);
}
